// SKADE-forvaltede rom + ORBAT-drevet rom-utledning (spec: vale/FELTVEKSLER_ADMIN.md).
//
// Når en GNÅ-bruker bindes til en ORBAT-node, utleder vi HVILKE rom brukeren skal være
// medlem av. Kun SKADE-forvaltede rom (merket `no.veifo.gna.managed`) auto-provisjoneres;
// rom brukere lager selv røres aldri. Denne modulen er REN og deterministisk — selve
// Synapse-innmeldingen skjer i hub-en. Slik kan taksonomien enhetstestes uten Matrix.
import { z } from 'zod';
/** Room-state som merker et rom som SKADE-forvaltet. Provisjoneringen rører KUN disse. */
export const ManagedRomMeta = z.object({
    source: z.literal('skade'),
    /** Rekkevidde: nasjonal, distrikt, eller en enkelt ORBAT-node. */
    scope: z.enum(['nasjonal', 'distrikt', 'enhet']),
    /** Node-/distrikt-id rommet hører til (tomt for nasjonal). */
    node: z.string().default(''),
});
export const MANAGED_STATE_TYPE = 'no.veifo.gna.managed';
/**
 * Alias for det kanoniske Varsler-/kunngjøringsrommet. Dette rommet ER kilden GNÅ-appens
 * SoMe-feed («Varsler») rendrer (se lib/screens/notifications/feed_controller.dart —
 * `feedRoomNames`), og SKADE COP skal rendre det samme rommet som et Varsler-panel.
 * Én kilde, to visninger. Onboarding/«info» folder inn som et festet innlegg her —
 * ikke et eget rom (unngår to overlappende kringkastingsflater).
 */
export const VARSLER_ROM = 'gna-kunngjoringer';
/** Nasjonale «alle bør ha»-rom (§2). Alle GNÅ-brukere meldes inn. */
export const NASJONALE_ROM = [
    { alias: VARSLER_ROM, scope: 'nasjonal', tilgang: 'les' }, // Varsler-SoMe-feeden (skriv: admin/FOH)
    { alias: 'gna-nod', scope: 'nasjonal', tilgang: 'medlem' },
    { alias: 'gna-brukerstotte', scope: 'nasjonal', tilgang: 'medlem' },
];
/** Enhet-mal (§4): rom-typer hver ORBAT-node får.
 *
 * `ptt` er skilt fra `samband` med vilje: GNÅ-PTT kjører PÅ et rom, og en
 * trykk-og-snakk-kanal og en tekstlig sambandslogg har ulik bruk — man vil
 * kunne lese sambandet i ettertid uten å ha vært på lufta, og man vil kunne
 * bytte PTT-kanal uten å forlate sambandsrommet. Uten et eget rom må PTT låne
 * sambandsrommet, og da blir hver sending også en linje i loggen.
 */
export const ENHET_ROMTYPER = ['samband', 'ptt', 'ledelse'];
/** Normaliser en id til et trygt alias-ledd (a–z, 0–9, bindestrek). */
export function romLedd(s) {
    return s
        .toLowerCase()
        .replace(/æ/g, 'ae').replace(/ø/g, 'oe').replace(/å/g, 'aa')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
/** Distrikts-alias, f.eks. ('HV-01','ops') → 'hv01-ops'. */
export function distriktRom(distriktId, type) {
    return `${romLedd(distriktId)}-${romLedd(type)}`;
}
/** Enhets-alias, f.eks. ('HV-01','urs01','samband') → 'hv01-urs01-samband'. */
export function enhetRom(distriktId, nodeId, type) {
    return `${romLedd(distriktId)}-${romLedd(nodeId)}-${type}`;
}
/**
 * Utled hele rom-settet en bruker skal ha, gitt ORBAT-bindingen. Deterministisk og
 * duplikatfritt. Rekkefølge: nasjonalt → distrikt → egen node → underordnedes samband.
 * Ledelse-rom kun når `leder` er sann.
 */
export function romsettForNode(inn) {
    const ut = [...NASJONALE_ROM];
    const d = inn.distriktId;
    // §3 distriktsrom
    ut.push({ alias: distriktRom(d, 'kunngjoringer'), scope: 'distrikt', tilgang: 'les' });
    ut.push({ alias: distriktRom(d, 'ops'), scope: 'distrikt', tilgang: 'medlem' });
    // Etterretningsvarsler: ALLE i distriktet skal kunne MOTTA dem — skriverett
    // ligger hos stab/E-funksjonen (settes som power level ved provisjonering).
    // Uten et eget rom havner etterretning i ops-nettet og blandes med
    // sambandstrafikk, og da drukner varselet i det som ellers sies.
    ut.push({ alias: distriktRom(d, 'etterretning'), scope: 'distrikt', tilgang: 'les' });
    if (inn.leder)
        ut.push({ alias: distriktRom(d, 'stab'), scope: 'distrikt', tilgang: 'ledelse' });
    // §4 egen node
    if (inn.nodeId) {
        ut.push({ alias: enhetRom(d, inn.nodeId, 'samband'), scope: 'enhet', tilgang: 'medlem' });
        ut.push({ alias: enhetRom(d, inn.nodeId, 'ptt'), scope: 'enhet', tilgang: 'medlem' });
        if (inn.leder)
            ut.push({ alias: enhetRom(d, inn.nodeId, 'ledelse'), scope: 'enhet', tilgang: 'ledelse' });
    }
    // §5 underordnedes samband (les) — stab ser nedover, ikke motsatt
    for (const sub of inn.underordnede ?? []) {
        if (sub && sub !== inn.nodeId) {
            ut.push({ alias: enhetRom(d, sub, 'samband'), scope: 'enhet', tilgang: 'les' });
        }
    }
    // Duplikatfri på alias; sterkeste tilgang vinner (ledelse > medlem > les).
    const rang = { les: 0, medlem: 1, ledelse: 2 };
    const beste = new Map();
    for (const r of ut) {
        const eks = beste.get(r.alias);
        if (!eks || rang[r.tilgang] > rang[eks.tilgang])
            beste.set(r.alias, r);
    }
    return [...beste.values()];
}
//# sourceMappingURL=managed-rooms.js.map